// Company management utilities
import { COMPANIES, Company, Branch } from './companyData'
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase'

export interface Employee {
  id: string
  name: string
  email: string
  username?: string
  password?: string
  position: string
  companyId: number
  branchId: string
  status: string
  permissions: any
}

// Convert Supabase employee to app format
function convertSupabaseEmployee(supabaseEmployee: any): Employee {
  return {
    id: supabaseEmployee.id,
    name: supabaseEmployee.name,
    email: supabaseEmployee.email,
    username: supabaseEmployee.username,
    password: supabaseEmployee.password,
    position: supabaseEmployee.position,
    companyId: supabaseEmployee.company_id,
    branchId: supabaseEmployee.branch_id,
    status: supabaseEmployee.status,
    permissions: supabaseEmployee.permissions,
  }
}

// Convert app employee to Supabase format
function convertToSupabaseEmployee(employee: Employee): any {
  return {
    id: employee.id,
    name: employee.name,
    email: employee.email,
    username: employee.username,
    password: employee.password,
    position: employee.position,
    company_id: employee.companyId,
    branch_id: employee.branchId,
    status: employee.status,
    permissions: employee.permissions,
  }
}

// Get employees from Supabase ONLY
export async function getEmployees(): Promise<Employee[]> {
  if (!isSupabaseConfigured()) {
    throw new Error(
      'Supabase no está configurado. Por favor configura las variables de entorno.',
    )
  }

  try {
    console.log('📥 Fetching employees from Supabase...')
    const supabaseEmployees = await supabaseClient.getEmployees()
    const employees = supabaseEmployees.map(convertSupabaseEmployee)
    console.log(`✅ Loaded ${employees.length} employees from Supabase`)
    return employees
  } catch (error) {
    console.error('❌ Error fetching employees from Supabase:', error)
    throw error
  }
}

// Add new employee
export async function addEmployee(employee: Employee): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase no está configurado')
  }

  try {
    console.log('📤 Creating employee in Supabase...')
    const supabaseEmployee = convertToSupabaseEmployee(employee)
    await supabaseClient.createEmployee(supabaseEmployee)
    console.log('✅ Employee created in Supabase')
  } catch (error) {
    console.error('❌ Error creating employee in Supabase:', error)
    throw error
  }
}

// Update employee
export async function updateEmployee(
  id: string,
  updates: Partial<Employee>,
): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase no está configurado')
  }

  try {
    console.log(`📝 Updating employee ${id} in Supabase...`)
    const supabaseUpdates = convertToSupabaseEmployee(updates as Employee)
    await supabaseClient.updateEmployee(id, supabaseUpdates)
    console.log('✅ Employee updated in Supabase')
  } catch (error) {
    console.error('❌ Error updating employee in Supabase:', error)
    throw error
  }
}

// Delete employee
export async function deleteEmployee(id: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase no está configurado')
  }

  try {
    console.log(`🗑️ Deleting employee ${id} from Supabase...`)
    await supabaseClient.deleteEmployee(id)
    console.log('✅ Employee deleted from Supabase')
  } catch (error) {
    console.error('❌ Error deleting employee from Supabase:', error)
    throw error
  }
}

// Get employee count by company
export async function getEmployeeCountByCompany(
  companyId: number,
): Promise<number> {
  const employees = await getEmployees()
  return employees.filter((e) => e.companyId === companyId).length
}

// Get companies from localStorage (with branches)
export function getCompanies(): Company[] {
  const saved = localStorage.getItem('criket_companies')
  return saved ? JSON.parse(saved) : COMPANIES
}

// Save companies to localStorage
export function saveCompanies(companies: Company[]): void {
  localStorage.setItem('criket_companies', JSON.stringify(companies))
}

// Get branches by company ID
export function getBranchesByCompany(companyId: number): Branch[] {
  const companies = getCompanies()
  const company = companies.find((c) => c.id === companyId)
  return company?.branches || []
}

// Add new company
export function addCompany(company: Omit<Company, 'id'>): Company {
  const companies = getCompanies()
  const newId = Math.max(...companies.map((c) => c.id), 0) + 1
  const newCompany: Company = { ...company, id: newId }
  saveCompanies([...companies, newCompany])
  return newCompany
}

// Update company
export function updateCompany(id: number, updates: Partial<Company>): void {
  const companies = getCompanies()
  const updated = companies.map((c) => (c.id === id ? { ...c, ...updates } : c))
  saveCompanies(updated)
}

// Add branch to company
export function addBranchToCompany(
  companyId: number,
  branch: Omit<Branch, 'id' | 'companyId'>,
): void {
  const companies = getCompanies()
  const company = companies.find((c) => c.id === companyId)
  if (!company) return

  const newBranch: Branch = {
    ...branch,
    id: `${companyId}-${Date.now()}`,
    companyId,
  }

  company.branches.push(newBranch)
  saveCompanies(companies)
}

// Remove branch from company
export function removeBranchFromCompany(
  companyId: number,
  branchId: string,
): void {
  const companies = getCompanies()
  const company = companies.find((c) => c.id === companyId)
  if (!company) return

  company.branches = company.branches.filter((b) => b.id !== branchId)
  saveCompanies(companies)
}
